import { defineStore } from "pinia";
import axiosIns from "@/composables/axios";
import { useNotificationStore } from "./notification";
import { ITreatment } from "./treatment";

export interface IBudget {
  id?: number | null;
  budget_responsable: string | null;
  description: string | null;
  treatments: Array<ITreatment>;
  date: null | string;
  observation: string | null;
  status: string | null;
  amount_total: string | number | null;
  discount: number | null;
  discount_type: string | null;
  amount_discounted: number | string | null;
  patientId: number;
  upfront: number | null | string;
  installments: Array<any> | null;
  installment_value: number | null;
}

export const useBudgetStore = defineStore("budgets", {
  state: (): { budgets: IBudget[] } => ({
    budgets: [] as IBudget[],
  }),

  actions: {
    async createBudget(newBudget: any) {
      try {
        const res = await axiosIns.post("budgets", newBudget);
        await this.getBudgetByPatientId(newBudget.patientId);
        const notificationStore = useNotificationStore();
        notificationStore.notifySuccess({
          title: "Sucesso",
          text: "Orçamento criado!",
        });
        return res.data;
      } catch (error) {
        console.error("Erro ao criar orçamento:", error);
        const notificationStore = useNotificationStore();
        notificationStore.notifyFail({
          title: "Erro",
          text: "Erro ao criar orçamento",
        });
        throw error;
      }
    },

    async getAllBudgets() {
      return axiosIns
        .get("budgets", { params: { excludeDeleted: true } })
        .then((res: any) => (this.budgets = res.data))
        .catch((error) => {
          console.error("Error getting orçamentos:", error);
        });
    },

    async getBudgetByPatientId(id: number) {
      return axiosIns
        .get(`budgets/patient/${id}`, { params: { excludeDeleted: true } })
        .then((res: any) => (this.budgets = res.data))
        .catch((error) => {
          console.error("Error getting budget:", error);
        });
    },

    async getBudgetById(id: number) {
      return axiosIns
        .get(`budgets/${id}`)
        .then((res: any) => res.data)
        .catch((error) => {
          console.error("Error getting budget:", error);
        });
    },

    async updateBudget(id: number, updatedBudget: IBudget) {

      try {
        const res = await axiosIns.put(`budgets/${id}`, updatedBudget);
        await this.getBudgetByPatientId(updatedBudget.patientId);
        const notificationStore = useNotificationStore();
        notificationStore.notifySuccess({
          title: "Sucesso",
          text: "Orçamento atualizado.",
        });
        return res.data;
      } catch (error) {
        console.error("Error updating budget:", error);
        const notificationStore = useNotificationStore();
        notificationStore.notifyFail({
          title: "Erro",
          text: "Erro ao atualizar orçamento",
        });
        throw error;
      }
    },
    async updateBudgetStatus(id: number, status: string) {
      try {
        const res = await axiosIns.put(`budgets/${id}/status`, { status });
        await this.getBudgetByPatientId(res.data.patientId);
        const notificationStore = useNotificationStore();
        notificationStore.notifySuccess({
          title: "Sucesso",
          text: "Status atualizado.",
        });
        return res.data;
      } catch (error) {
        console.error("Error updating status:", error);
        const notificationStore = useNotificationStore();
        notificationStore.notifyFail({
          title: "Erro",
          text: "Erro ao atualizar status",
        });
        throw error;
      }
    },

    async deleteBudget(id: number) {
      axiosIns
        .delete(`budgets/${id}`)
        .then((res) => {
          const notificationStore = useNotificationStore();
          notificationStore.notifySuccess({
            title: "Sucesso",
            text: "Orçamento deletado.",
          });
          this.getBudgetByPatientId(res.data.patientId);
        })
        .catch((error) => {
          console.error("Error deleting budget:", error);
          const notificationStore = useNotificationStore();
          notificationStore.notifyFail({
            title: "Erro",
            text: "Erro ao deletar orçamento",
          });
        });
    },
  },
});
